'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { Box, Link, SxProps, Theme } from '@mui/material'
import { isAeDomain } from '@/src/utils/generalUtils/isAeDomain'
import { isEgDomain } from '@/src/utils/generalUtils/isEgDomain'

interface VatBadgeProps {
    size?: number
    sx?: SxProps<Theme>
}

const VAT_CERT_URL = 'https://ui.cltp.co/flyin-static/docs/flyin-VAT-cert.pdf'
const VAT_IMAGE_URL = 'https://ui.cltp.co/flyin-static/img/vat-cert-logo.png'

/**
 * Reusable VAT badge used across all footers.
 * Client component - automatically hides for AE and EG domains.
 * Checks domain on client-side after mount to avoid SSR issues.
 */
export const VatBadge: React.FC<VatBadgeProps> = ({ size = 32, sx }) => {
    const [shouldHide, setShouldHide] = useState(false)

    useEffect(() => {
        // Check domain on client-side after mount
        const isAe = isAeDomain()
        const isEg = isEgDomain()
        setShouldHide(isAe || isEg)
    }, [])

    if (shouldHide) {
        return null
    }

    return (
        <Link href={VAT_CERT_URL} target="_blank" aria-label="VAT certificate">
            <Box sx={{ width: size, height: size, display: 'inline-flex', ...sx }}>
                <Image
                    src={VAT_IMAGE_URL}
                    alt="VAT certificate"
                    width={size}
                    height={size}
                    style={{ width: '100%', height: '100%' }}
                    loading="lazy"
                    priority={false}
                    unoptimized
                />
            </Box>
        </Link>
    )
}

export default VatBadge
